import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Plus, FileText } from 'lucide-react';
import { radiologyOrderService, radiologyOrderItemService, RadiologyOrder } from '../../lib/services.radiology';
import toast from 'react-hot-toast';

interface OrderWithDetails extends RadiologyOrder {
  itemCount?: number;
}

export default function RadiologyWorklist() {
  const [orders, setOrders] = useState<OrderWithDetails[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<OrderWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'ordered' | 'scheduled' | 'in_progress' | 'reported' | 'finalized'>('ordered');
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadOrders();
  }, []);

  useEffect(() => {
    filterOrders();
  }, [orders, activeTab, search]);

  const loadOrders = async () => {
    try {
      const ordersData = await radiologyOrderService.getAll();
      
      const ordersWithDetails = await Promise.all(
        ordersData.map(async (order) => {
          try {
            const items = await radiologyOrderItemService.getByOrder(order.id);
            return {
              ...order,
              itemCount: items.length,
            };
          } catch (error) {
            return { ...order, itemCount: 0 };
          }
        })
      );

      setOrders(ordersWithDetails as OrderWithDetails[]);
    } catch (error) {
      console.error('Failed to load orders:', error);
      toast.error('Failed to load radiology orders');
    } finally {
      setLoading(false);
    }
  };

  const filterOrders = () => {
    let filtered = orders;

    const statusMap = {
      ordered: 'ordered',
      scheduled: 'scheduled',
      in_progress: 'in_progress',
      reported: 'reported',
      finalized: 'finalized'
    };

    filtered = filtered.filter(order => 
      order.status === statusMap[activeTab]
    );

    if (search) {
      filtered = filtered.filter(order =>
        order.patient?.name.toLowerCase().includes(search.toLowerCase()) ||
        order.order_no.toLowerCase().includes(search.toLowerCase())
      );
    }

    setFilteredOrders(filtered);
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, string> = {
      ordered: 'bg-yellow-100 text-yellow-800',
      scheduled: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-purple-100 text-purple-800',
      reported: 'bg-green-100 text-green-800',
      finalized: 'bg-gray-100 text-gray-800',
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityBadge = (priority: string) => {
    const badges: Record<string, string> = {
      stat: 'bg-red-100 text-red-800',
      urgent: 'bg-orange-100 text-orange-800',
      routine: 'bg-green-100 text-green-800',
    };
    return badges[priority] || 'bg-gray-100 text-gray-800';
  };

  const getPaymentBadge = (paymentStatus?: string) => {
    const badges: Record<string, string> = {
      paid: 'bg-green-100 text-green-800',
      partial: 'bg-yellow-100 text-yellow-800',
      pending: 'bg-red-100 text-red-800'
    };
    return badges[paymentStatus || 'pending'] || 'bg-gray-100 text-gray-800';
  };

  const getTabCounts = () => {
    return {
      ordered: orders.filter(o => o.status === 'ordered').length,
      scheduled: orders.filter(o => o.status === 'scheduled').length,
      in_progress: orders.filter(o => o.status === 'in_progress').length,
      reported: orders.filter(o => o.status === 'reported').length,
      finalized: orders.filter(o => o.status === 'finalized').length,
    };
  };

  const tabs = [
    { key: 'ordered' as const, label: 'Ordered' },
    { key: 'scheduled' as const, label: 'Scheduled' },
    { key: 'in_progress' as const, label: 'In Progress' },
    { key: 'reported' as const, label: 'Reported' },
    { key: 'finalized' as const, label: 'Finalized' },
  ];

  const counts = getTabCounts();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Radiology Worklist</h1>
          <p className="text-gray-600">Manage imaging orders and studies</p>
        </div>
        <Link
          to="/radiology/new"
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          New Order
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        <div className="border-b border-gray-200">
          <div className="flex">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-6 py-3 text-sm font-medium ${
                  activeTab === tab.key
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label} ({counts[tab.key]})
              </button>
            ))}
          </div>
        </div>

        <div className="p-4">
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by patient name or order number..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order No</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Patient</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tests</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {order.order_no}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {order.patient?.name || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {order.itemCount} test(s)
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${getPriorityBadge(order.priority)}`}>
                        {order.priority.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(order.status)}`}>
                        {order.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${getPaymentBadge((order as any).payment_status)}`}>
                        {((order as any).payment_status || 'pending').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {new Date(order.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <Link
                        to={`/radiology/report/${order.id}`}
                        className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                      >
                        <FileText className="w-4 h-4" />
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredOrders.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                No orders found
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
